import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Wraps a Pro-only route for solo (general_client) users. Coaches and
 * athletes pass through untouched. If a solo user is Pro (active pro_until),
 * they see the real page. Free solo users see an inline upgrade nudge with a
 * deep-link to the matching benefit row on the Upgrade page.
 *
 * Usage: <ProGuard slug="form-check" label="AI Form Check"><FormCheck /></ProGuard>
 */
export default function ProGuard({ slug, label, children }) {
  const { user } = useAuth();

  // Only gate solo/general_client users.
  const primaryRole = user?.primary_role;
  if (primaryRole !== "general_client") return children;

  const proUntilRaw = user?.pro_until;
  const isProActive = !!proUntilRaw && new Date(proUntilRaw).getTime() > Date.now();
  if (isProActive) return children;

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div
        className="tactical-card p-8 md:p-10 max-w-xl w-full text-center relative overflow-hidden"
        data-testid="pro-guard-nudge"
      >
        <div className="w-14 h-14 mx-auto mb-4 border border-brand-heat flex items-center justify-center bg-brand-heat/10">
          <Lock size={22} weight="fill" className="text-brand-heat" />
        </div>
        <div className="label-tiny text-brand-heat text-[11px] mb-2">// PRO SUBSCRIBERS ONLY</div>
        <h2 className="font-heading text-2xl md:text-3xl mb-3">
          {label ? label.toUpperCase() : "THIS TOOL"} IS PART OF <span className="text-heat neon-yellow">EDGE PRO</span>
        </h2>
        <p className="text-sm text-text-secondary mb-6 max-w-md mx-auto">
          Free plans keep the essentials. This tool ships with Edge Pro alongside
          unlimited AI programs, deep analytics, and priority Sonnet 4.5 access.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to={`/upgrade?tab=athlete${slug ? `&highlight=${slug}` : ""}`}
            className="btn-kinetic px-5 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-2"
            data-testid="pro-guard-upgrade-cta"
          >
            SEE WHAT PRO UNLOCKS
            <ArrowRight size={12} weight="bold" />
          </Link>
          <Link
            to="/client"
            className="text-[11px] label-tiny tracking-widest text-text-secondary hover:!text-brand-accent transition-colors inline-flex items-center gap-1.5"
            data-testid="pro-guard-back-link"
          >
            <Sparkle size={11} weight="fill" /> BACK TO DASHBOARD
          </Link>
        </div>
      </div>
    </div>
  );
}
